import { useEffect, useState } from 'react';
import { ThemeProvider } from 'next-themes';
import ModernDashboard from './components/modern/ModernDashboard';
import { LoginPage } from './components/auth/LoginPage';
import { RegisterPage } from './components/auth/RegisterPage';
import HomePage from './components/home/HomePage';
import SplashScreen from './components/home/SplashScreen';
import { SharedScenarioPage } from './components/trade-viewer/SharedScenarioPage';
import { SharedBacktestRunPage } from './components/automation/SharedBacktestRunPage';
import { SharedComparisonPage } from './components/automation/SharedComparisonPage';
import { UpgradePrompt } from '@/components/plan/UpgradePrompt';
import LegalPage from './components/legal/LegalPage';
import { ForgotPasswordPage, ResetPasswordPage } from './components/auth/PasswordResetPages';
import { BuildVersionBadge } from './components/BuildVersionBadge';
import { useAuth } from './auth/AuthContext';

const REDIRECT_KEY = 'ss.auth.redirect';

type Route =
  | { kind: 'home' }
  | { kind: 'login' }
  | { kind: 'register' }
  | { kind: 'shared' }
  | { kind: 'shared-backtest' }
  | { kind: 'shared-comparison' }
  | { kind: 'legal' }
  | { kind: 'forgot-password' }
  | { kind: 'reset-password' }
  | { kind: 'dashboard' }
  | { kind: 'legacy'; legacyPath: string };

// readRoute parses window.location.hash into one of five buckets.
// 'dashboard' covers anything under #/dashboard (gated by auth).
// 'legacy' covers older flat hashes (e.g. #/scanner, #/configuration/...) and
// gets one-shot migrated to the new #/dashboard/<rest> shape.
function readRoute(): Route {
  const raw = window.location.hash || '';
  const stripped = raw.replace(/^#\/?/, '');
  const beforeQuery = stripped.split('?')[0];
  const first = beforeQuery.split('/')[0];

  if (first === '') return { kind: 'home' };
  if (first === 'login') return { kind: 'login' };
  if (first === 'register') return { kind: 'register' };
  // Password recovery — outside the auth gate by definition: the whole point is that the
  // visitor CANNOT sign in.
  if (first === 'forgot-password') return { kind: 'forgot-password' };
  if (first === 'reset-password') return { kind: 'reset-password' };
  // Public scenario share-link target — matched before 'dashboard' so it
  // stays outside the auth gate.
  if (first === 'shared-scenario') return { kind: 'shared' };
  // Public backtest-run share-link target — rich read-only run detail, also
  // matched before 'dashboard' so it stays outside the auth gate.
  if (first === 'backtest-run') return { kind: 'shared-backtest' };
  // Public comparison share-link target — read-only scoreboard, outside the auth gate.
  if (first === 'shared-comparison') return { kind: 'shared-comparison' };
  if (first === 'dashboard') return { kind: 'dashboard' };
  // Legal pages (terms / privacy / risk). Outside the auth gate on purpose: a visitor must
  // be able to read the terms BEFORE creating an account, and Stripe links here from checkout.
  if (first === 'legal') return { kind: 'legal' };
  // Anything else is a stale legacy URL that should migrate to the new prefix.
  // Preserve the full path + query so e.g. `#/trade-viewer?scenario=abc`
  // becomes `#/dashboard/trade-viewer?scenario=abc`.
  return { kind: 'legacy', legacyPath: stripped };
}

function AppRouter() {
  const { isAuthenticated, isLoading } = useAuth();
  const [route, setRoute] = useState<Route>(() => readRoute());

  useEffect(() => {
    const onHash = () => setRoute(readRoute());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Wait for auth hydration before deciding — otherwise we'd flash the
  // login redirect for users who actually have a valid session.
  if (isLoading) {
    return <SplashScreen />;
  }

  // Public auth forms — anyone can hit them.
  if (route.kind === 'login') return <LoginPage />;
  if (route.kind === 'register') return <RegisterPage />;

  // Public scenario share link — read-only trade viewer, no account needed.
  // The token in ?key= is the credential; resolution happens in-page.
  if (route.kind === 'shared') return <SharedScenarioPage />;

  // Public backtest-run share link — read-only rich run detail, no account needed.
  if (route.kind === 'shared-backtest') return <SharedBacktestRunPage />;

  // Public comparison share link — read-only scoreboard, no account needed.
  if (route.kind === 'shared-comparison') return <SharedComparisonPage />;

  // Marketing home is public — signed-in users see it too, just with a
  // "Go to dashboard" CTA in place of the sign-in buttons.
  if (route.kind === 'legal') return <LegalPage />;
  if (route.kind === 'forgot-password') return <ForgotPasswordPage />;
  if (route.kind === 'reset-password') return <ResetPasswordPage />;

  if (route.kind === 'home') {
    return <HomePage />;
  }

  // One-shot migration of legacy flat hashes (e.g. #/scanner) to the new
  // #/dashboard/<rest> shape. Public scenario share links use the explicit
  // #/shared-scenario route matched above, so they never reach this branch.
  if (route.kind === 'legacy') {
    window.location.hash = `#/dashboard/${route.legacyPath}`;
    return null;
  }

  // route.kind === 'dashboard' — gate behind auth.
  if (!isAuthenticated) {
    try {
      // Save the requested hash so LoginPage can bounce the user back
      // after a successful sign-in (LoginPage already reads this key).
      localStorage.setItem(REDIRECT_KEY, window.location.hash);
    } catch {
      /* swallow */
    }
    window.location.hash = '#/login';
    return null;
  }
  return <ModernDashboard />;
}

function App() {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={true}
      disableTransitionOnChange={false}
    >
      <AppRouter />
      {/* Mounted once at the shell so a 402 from ANY of the ~20 services surfaces as an
          upgrade prompt rather than a raw JSON blob in whatever screen happened to make
          the call. */}
      <UpgradePrompt />
      <BuildVersionBadge />
    </ThemeProvider>
  );
}

export default App;
